import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  Plus,
  Loader2,
  Diamond,
  Sun,
  PersonStanding,
  ScanFace,
} from "lucide-react";
import type { ComponentType } from "react";
import { cn } from "@/lib/utils";
import { useTryOnPhotos } from "../hooks/use-try-on-photos";
import { TryOnPhotoCard } from "../components/TryOnPhotoCard";
import { TryOnPhotoDeleteDialog } from "../components/TryOnPhotoDeleteDialog";

const TIPS: {
  key: string;
  icon: ComponentType<{ className?: string }>;
}[] = [
  { key: "angles", icon: Diamond },
  { key: "lighting", icon: Sun },
  { key: "fullBody", icon: PersonStanding },
  { key: "faceVisibility", icon: ScanFace },
];

interface AddPhotoSlotProps {
  isAdding: boolean;
  showLabels: boolean;
  onClick: () => void;
}

const AddPhotoSlot: React.FC<AddPhotoSlotProps> = ({
  isAdding,
  showLabels,
  onClick,
}) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isAdding}
      aria-label={t("settings.profile.tryOn.addPhoto")}
      className={cn(
        "@container/slot flex min-w-0 aspect-[3/4] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border-secondary dark:border-gray-600 text-text-tertiary dark:text-gray-400 transition-colors",
        isAdding
          ? "cursor-wait opacity-70"
          : "hover:border-text-brand/60 dark:hover:border-indigo-400/60 hover:text-text-brand dark:hover:text-indigo-400 hover:bg-indigo-50/40 dark:hover:bg-indigo-500/10"
      )}
    >
      <span className="flex h-8 w-8 @min-[140px]/slot:h-9 @min-[140px]/slot:w-9 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
        {isAdding ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Plus className="h-4 w-4" />
        )}
      </span>
      {showLabels && (
        <>
          <span className="px-1 text-center text-xs @min-[140px]/slot:text-sm font-medium leading-tight">
            {isAdding
              ? t("settings.profile.tryOn.uploading")
              : t("settings.profile.tryOn.addPhoto")}
          </span>
          <span className="hidden @min-[120px]/slot:block px-1.5 text-center text-[10px] @min-[160px]/slot:text-[11px] leading-4">
            {t("settings.profile.tryOn.addPhotoHint")}
          </span>
        </>
      )}
    </button>
  );
};

/**
 * Try-On Photos Section
 * AI kıyafet deneme için fotoğraf galerisi. Kullanıcı yalnızca kendi
 * fotoğraflarını değil eşinin/çocuklarının fotoğraflarını da tutabilir;
 * her fotoğrafın adı kart üzerinde düzenlenebilir. `isSelf` işaretli
 * fotoğraf try-on için ana görseldir.
 */
export const TryOnPhotosSection: React.FC = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { photos, limit, isLoading, isAdding, addPhoto } = useTryOnPhotos();
  const emptySlotCount = Math.max(limit - photos.length, 0);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    void addPhoto(file);
  };

  return (
    <section
      className={cn(
        "relative rounded-2xl p-px",
        "bg-gradient-to-br from-indigo-300/70 via-border-secondary to-border-secondary/40",
        "dark:from-indigo-400/40 dark:via-gray-600/50 dark:to-gray-700/30",
        "shadow-[0_16px_48px_-16px_rgba(79,70,229,0.25)]",
        "dark:shadow-[0_16px_48px_-16px_rgba(99,102,241,0.45)]"
      )}
    >
      <div className="relative overflow-hidden rounded-[15px] bg-white dark:bg-gray-800">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-50/90 via-transparent to-transparent dark:from-indigo-500/15 dark:via-transparent" />
        <div className="pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full bg-indigo-200/40 blur-3xl dark:bg-indigo-500/15" />

        <div className="relative flex flex-col gap-4 md:gap-5 p-4 sm:p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-text-brand dark:text-indigo-400">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex flex-col gap-0.5">
                <h3 className="text-sm sm:text-base font-semibold text-text-primary dark:text-gray-100 leading-6">
                  {t("settings.profile.tryOn.title")}
                </h3>
                <p className="text-xs sm:text-sm text-text-tertiary dark:text-gray-400 leading-5 max-w-xl">
                  {t("settings.profile.tryOn.description")}
                </p>
              </div>
            </div>

            <span className="shrink-0 rounded-lg bg-gray-100 dark:bg-gray-900/70 px-2.5 py-1 text-xs font-semibold text-text-secondary dark:text-gray-300 tabular-nums">
              {photos.length}/{limit}
            </span>
          </div>

          <div className="@container">
            <div className="grid grid-cols-2 @min-[560px]:grid-cols-4 gap-2 @min-[420px]:gap-3">
              {isLoading &&
                Array.from({ length: limit }).map((_, index) => (
                  <div
                    key={index}
                    className="min-w-0 aspect-[3/4] rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse"
                  />
                ))}

              {!isLoading &&
                photos.map((photo) => (
                  <TryOnPhotoCard key={photo.id} photo={photo} />
                ))}

              {!isLoading &&
                Array.from({ length: emptySlotCount }).map((_, index) => (
                  <AddPhotoSlot
                    key={`empty-${index}`}
                    isAdding={isAdding}
                    showLabels={index === 0}
                    onClick={() => fileInputRef.current?.click()}
                  />
                ))}
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png, image/jpeg"
            onChange={handleFileSelect}
            className="sr-only"
          />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 rounded-xl border border-border-secondary dark:border-gray-600 bg-white/60 dark:bg-gray-900/40 p-3.5 sm:p-4">
            {TIPS.map(({ key, icon: Icon }) => (
              <div key={key} className="flex flex-col gap-1.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-xs sm:text-sm font-semibold text-text-primary dark:text-gray-100">
                  {t(`settings.profile.tryOn.tips.${key}.title`)}
                </span>
                <span className="text-[11px] sm:text-xs text-text-tertiary dark:text-gray-400 leading-4">
                  {t(`settings.profile.tryOn.tips.${key}.description`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <TryOnPhotoDeleteDialog />
    </section>
  );
};
